import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { CampaignStepType, CampaignWaitUnit } from '../../constants/campaign-sequence';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignEntity } from './campaign.entity';

/**
 * PD-17613 — one step of a campaign's sequence: Email, Call, LinkedIn,
 * Voicemail drop or Task, in any order. The wait lives on the step it follows
 * (Instantly's rule): `waitAmount` + `waitUnit` is how long after THIS step
 * is done the next one comes due.
 *
 * `stepNo` is the position (1-based) among the campaign's live steps. Editing
 * a running campaign changes only steps people haven't reached (PD-17625); a
 * removed step is soft-deleted so the history rows pointing at it still
 * resolve.
 */
@Index('campaign_step__campaign_step_no', ['fkCampaignId', 'stepNo'], {})
@Entity('campaign_step', {}, { auditable: true })
export class CampaignStepEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	@Column('int', { name: 'stepNo', unsigned: true })
	public stepNo!: number;

	// App-validated (CampaignStepType).
	@Column('varchar', { name: 'type', length: 20 })
	public type!: CampaignStepType | string;

	/** The wait after this step before the next comes due. 0 = right away. */
	@Column('int', { name: 'waitAmount', unsigned: true, default: () => '0' })
	public waitAmount!: number;

	// App-validated (CampaignWaitUnit).
	@Column('varchar', { name: 'waitUnit', length: 20, default: () => "'business_days'" })
	public waitUnit!: CampaignWaitUnit | string;

	/** The campaign version of this step's copy (an email, a call script, a LinkedIn draft). */
	@Column('int', { name: 'fk_campaign_content__id', unsigned: true, nullable: true })
	public fkCampaignContentId!: number | null;

	/**
	 * Per-type settings. email: { thread: 'same' | 'new' }; call: { doneRule,
	 * voicemailDrop, caller }; linkedin: { action: 'connect' | 'message' };
	 * task: { text, assignee }.
	 */
	@Column('json', { name: 'settings', nullable: true })
	public settings!: Record<string, unknown> | null;

	/**
	 * For an email after the first: the shared Instantly label that releases
	 * it and the subsequence it starts, set when the campaign starts (also
	 * kept on campaign_provider_campaign.subsequences).
	 */
	@Column('varchar', { name: 'releaseLabel', length: 120, nullable: true })
	public releaseLabel!: string | null;

	@Column('varchar', { name: 'providerSubsequenceId', length: 64, nullable: true })
	public providerSubsequenceId!: string | null;

	@ManyToOne(() => CampaignEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign__id', referencedColumnName: 'id' }])
	public fkCampaign!: CampaignEntity;
}
